import { ElectronApi } from '@/services/electron.api';
import { Shape, ShapeType } from '@/services/shape';
import { WatchfaceService } from '@/services/watchface';
import { WatchfaceRenderer } from '@/watch-renderer';
import {
  decodeFont,
  decodeImage,
  EventType,
  FontAsset,
  ImageAsset,
  Rive
} from '@rive-app/canvas';
import { useImageDoms, useShapeVideo } from '.';
import { Description } from '@/types/watchface';
import { loadImageDom } from '@/utils/fs/image.utils';
import { useImagesStore } from '@/store/use-images-store';

/**
 * 获取当前样式下的rive图层，一个theme下仅能有一个rive
 * @param theme
 * @param cacheEnabled 是否可以mainRenderer中的缓存
 * @returns
 */
export const getThemeRive = async (data: {
  theme: StoreThemeItem;
  wathchOS: Description['watchOS'];
  renderer: WatchfaceRenderer | null | undefined;
  autoLoad?: boolean;
  useRivePreview?: boolean;
}) => {
  const { fs, path } = ElectronApi;
  if (!fs || !path) return;

  const { theme, wathchOS, renderer, autoLoad, useRivePreview = false } = data;

  // 一个theme下仅能有一个rive，查找第一个即可
  const riveLayer = theme.children.find((item) => item.type == ShapeType.Rive);
  if (!riveLayer) return;

  const shape = Shape.getShape(riveLayer.id);
  const {
    riveFile,
    riveFileExt,
    riveToShow = 'normal',
    w = 100,
    h = 100,
    resources
  } = shape?.attrs ?? {};

  // 需要判断要显示的rive文件类型
  const riveFilename =
    riveToShow == 'ext' && riveFileExt ? riveFileExt : riveFile;

  // 如果当前render中已存在，则直接使用，不需要重新创建
  // 通过riveFilename&themeName作为唯一标识进行匹配（不同theme下同一rive表现不一样），需要在创建rive图层时，保证不同riveFilename不一样
  if (renderer) {
    if (
      renderer?.runtime.rive?.filename == riveFilename &&
      renderer?.runtime.renderTheme.name === theme.name
    ) {
      return renderer?.runtime.rive;
    } else {
      // 未获取都缓存，需要将已有的清空
      renderer?.runtime.rive?.rive?.stopRendering();
    }
  }
  if (!riveFilename) return;

  const service = new WatchfaceService();
  const _riveFilename = riveFilename
    ? service.resource.parseFilename(riveFilename)
    : '';
  if (!riveFilename || !_riveFilename) return;

  const fileBf = await fs.promises.readFile(_riveFilename);
  const canvasOffscreen = new OffscreenCanvas(w, h);

  // 获取rive的图片资源
  const riveDir = path.dirname(_riveFilename);
  const riveResources = await fs.promises.readdir(riveDir);

  const _loadImageAsset = async (
    asset: ImageAsset,
    resourceBasename: string
  ) => {
    const ab = await fs.promises.readFile(path.join(riveDir, resourceBasename));
    const image = await decodeImage(new Uint8Array(ab.buffer));
    asset.setRenderImage(image);
    image.unref();
  };

  const _loadFontAsset = async (asset: FontAsset, resourceBasename: string) => {
    const ab = await fs.promises.readFile(path.join(riveDir, resourceBasename));
    const font = await decodeFont(new Uint8Array(ab.buffer));
    asset.setFont(font);
    font.unref();
  };

  const r = new Rive({
    canvas: canvasOffscreen,
    buffer: fileBf.buffer as ArrayBuffer,
    stateMachines: 'State Machine 1',
    autoplay: true,
    assetLoader: (asset) => {
      // Here, we load a font asset with a random image on load of the Rive file
      // and return true, because this callback handler is responsible for loading
      // the asset, as opposed to the runtime
      const resourceName = asset.uniqueFilename;
      if (riveResources.includes(resourceName)) {
        if (asset.isImage) {
          _loadImageAsset(asset as ImageAsset, resourceName);
          return true;
        } else if (asset.isFont) {
          _loadFontAsset(asset as FontAsset, resourceName);
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    }
  });

  let _rivePreviewImgEl = undefined;
  if (resources?.rivePreview && useRivePreview) {
    const imageStore = useImagesStore();

    const rivePreviewKey = Object.keys(resources.rivePreview)[0];
    const rivePreviewFileName = resources.rivePreview[rivePreviewKey][0];

    const rivePreviewImg = imageStore.imgs.find(
      (item) => item.basename === rivePreviewFileName
    );
    const base64 = rivePreviewImg?.value;

    if (base64) {
      _rivePreviewImgEl = await loadImageDom(base64);
    }
  }

  const res: WatchRendererOptions['rive'] = {
    filename: riveFilename,
    rive: r,
    canvas: canvasOffscreen,
    useRivePreview
  };

  if (_rivePreviewImgEl) {
    res.rivePreviewImgEl = _rivePreviewImgEl;
  }

  if (autoLoad) {
    return new Promise<WatchRendererOptions['rive']>((resolve, reject) => {
      r.on(EventType.Load, () => {
        resolve(res);
      });
      r.on(EventType.LoadError, (e) => {
        reject(e);
      });
    });
  } else {
    return res;
  }
};

// 图片doms，当doms改变后重新render
export const getImageAndVideoDoms = async (data: {
  themeId: string;
  colorId: string;
  wholeFrameIds?: string[];
  watchfaceAst: Ast;
}) => {
  const { getImgDoms } = useImageDoms();
  const { toVideoDoms, getVideoSrcsFromAst } = useShapeVideo();
  const { themeId, wholeFrameIds, colorId, watchfaceAst } = data;
  // const wholeImagesframeSrcs: string[] = [];
  // if (wholeFrameIds?.length) {
  //   wholeFrameIds.forEach((id) => {
  //     const shape = Shape.getShape(id);
  //     if (shape?.attrs.spriteType == 'video') return;
  //     const srcs = shape?.attrs.resources?.images?.[colorId ?? ''] ?? [];
  //     wholeImagesframeSrcs.push(...srcs);
  //   });
  // }
  const images = await getImgDoms({
    themeId,
    colorId,
    keepWholeImagesSpriteIds: wholeFrameIds
  });

  // 获取 vidoe doms数据
  const videoSrcs = getVideoSrcsFromAst(watchfaceAst);
  const videos = await toVideoDoms(videoSrcs, {
    autoLoadData: true
  });

  return { images, videos };
};
